import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Progress } from '@backstage/core-components';
import { DocTreeNodeRow } from './tree/DocTreeNodeRow';
import { useEntityDocTree } from './tree/useEntityDocTree';

/*
 * A glimpse of the same catalog-wide tree the Search page's Tree tab shows in
 * full — top-level Domains/Systems only, collapsed, so this card stays a
 * quick preview rather than a second full tree viewer. The hero's old "Just
 * show me the tree!" button read as a form submit next to the search field;
 * a card with its own "Browse the full tree" link at the bottom reads as
 * exactly what it is instead.
 */
const MAX_TOP_LEVEL = 6;

const useStyles = makeStyles({
  panel: {
    borderRadius: 12,
    background: 'var(--bui-white)',
    boxShadow: 'var(--hone-portal-elevation-card)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
    fontSize: 13.5,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
  body: {
    padding: '8px 12px',
  },
  empty: {
    margin: 0,
    padding: '18px 20px',
    fontSize: 12.5,
    color: 'var(--hone-portal-fg-tertiary)',
  },
  more: {
    display: 'block',
    padding: '12px 20px',
    borderTop: '1px solid var(--hone-portal-hairline-row)',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'none',
    '&:hover': {
      background: 'var(--bui-bg-neutral-2)',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'inset 0 0 0 2px #039cfb',
    },
  },
});

export const DocsTreePreviewCard = () => {
  const classes = useStyles();
  const { value, loading } = useEntityDocTree();

  if (loading) return <Progress />;

  const nodes = (value ?? []).slice(0, MAX_TOP_LEVEL);

  return (
    <section className={classes.panel}>
      <header className={classes.header}>Documentation tree</header>
      {nodes.length === 0 ? (
        <p className={classes.empty}>No documented entities yet.</p>
      ) : (
        <div className={classes.body}>
          {nodes.map(node => (
            <DocTreeNodeRow key={node.key} node={node} depth={0} autoExpand={false} />
          ))}
        </div>
      )}
      <RouterLink to="/search?tab=tree" className={classes.more}>
        Browse the full tree ▸
      </RouterLink>
    </section>
  );
};
